/** @format — Document preview with URL + fallback */

import { memo, useState } from "react";
import { FileText, Image as ImageIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import type { DocumentItem } from "../types";
import { isPhotoUploadType } from "../utils/documentFormatters";
import { resolveThumbnailSrc } from "../utils/resolveThumbnailSrc";

const PREVIEW_FRAME_BORDER =
  "rounded-tl-lg rounded-tr-lg border border-b-0 border-neutral-200";

function FallbackIcon({ isPhoto, size }: { isPhoto: boolean; size: "sm" | "lg" | "list" }) {
  const Icon = isPhoto ? ImageIcon : FileText;
  const iconClass =
    size === "sm" || size === "list"
      ? "h-4 w-4 text-neutral-400"
      : "h-8 w-8 text-neutral-300";
  return <Icon className={iconClass} strokeWidth={size === "sm" || size === "list" ? 1.75 : 1.5} />;
}

function DocumentThumbnail({
  document,
  size = "lg",
  variant = "default",
  className,
}: {
  document: DocumentItem;
  size?: "sm" | "lg" | "list";
  variant?: "default" | "card";
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const isPhoto = document.kind === "photo" || isPhotoUploadType(document.uploadType);
  const previewSrc = resolveThumbnailSrc(document, failed);

  if (size === "list") {
    return (
      <div
        className={cn(
          "h-10 w-10 shrink-0 overflow-hidden rounded-md bg-neutral-50",
          previewSrc ? "border border-neutral-200" : "",
          className,
        )}
        aria-hidden
      >
        {previewSrc ? (
          <img
            src={previewSrc}
            alt=""
            className="h-full w-full object-cover object-top"
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-neutral-50">
            <FallbackIcon isPhoto={isPhoto} size="list" />
          </div>
        )}
      </div>
    );
  }

  if (size === "sm") {
    return (
      <div
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-neutral-50",
          previewSrc ? "border border-neutral-200" : "",
          className,
        )}
        aria-hidden
      >
        {previewSrc ? (
          <img
            src={previewSrc}
            alt=""
            className="h-full w-full object-cover object-top"
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
          />
        ) : (
          <FallbackIcon isPhoto={isPhoto} size="sm" />
        )}
      </div>
    );
  }

  if (variant === "card") {
    return (
      <div
        className={cn("flex min-h-0 flex-1 self-stretch bg-neutral-50 px-2 pt-2", className)}
        aria-hidden
      >
        <div
          className={cn(
            "relative h-full min-h-0 w-full overflow-hidden bg-neutral-50",
            previewSrc ? PREVIEW_FRAME_BORDER : "rounded-tl-lg rounded-tr-lg",
          )}
        >
          {previewSrc ? (
            <img
              src={previewSrc}
              alt=""
              className="absolute inset-0 h-full w-full object-cover object-top"
              loading="lazy"
              decoding="async"
              onError={() => setFailed(true)}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-neutral-50">
              <FallbackIcon isPhoto={isPhoto} size="lg" />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex aspect-[3/4] w-full items-center justify-center overflow-hidden rounded-t-lg bg-neutral-50",
        previewSrc ? PREVIEW_FRAME_BORDER : "",
        className,
      )}
      aria-hidden
    >
      {previewSrc ? (
        <img
          src={previewSrc}
          alt=""
          className="h-full w-full object-cover object-top"
          loading="lazy"
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-neutral-50">
          <FallbackIcon isPhoto={isPhoto} size="lg" />
        </div>
      )}
    </div>
  );
}

export default memo(DocumentThumbnail);
